import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Sparkles, Mic, MicOff, Plus, Mail } from 'lucide-react';
import EmailWriterPanel from './EmailWriterPanel';

// Project Category Select Options
const ProjectCategoryOptions = ({ language }) => (
  <>
    {/* Residential Projects */}
    <SelectItem value="bathroom">{language === 'es' ? 'Remodelación de Baño' : 'Bathroom Remodeling'}</SelectItem>
    <SelectItem value="concrete_work">{language === 'es' ? 'Trabajo de Concreto' : 'Concrete (Driveway/Patio)'}</SelectItem>
    <SelectItem value="drain_line_replacement">{language === 'es' ? 'Reemplazo de Drenaje' : 'Drain Line Replacement'}</SelectItem>
    <SelectItem value="drywall_repair">{language === 'es' ? 'Reparación de Drywall' : 'Drywall Repair'}</SelectItem>
    <SelectItem value="electrical_panel_upgrade">{language === 'es' ? 'Actualización de Panel Eléctrico' : 'Electrical Panel Upgrade'}</SelectItem>
    <SelectItem value="fence_installation">{language === 'es' ? 'Instalación de Cerca' : 'Fence Installation'}</SelectItem>
    <SelectItem value="flooring">{language === 'es' ? 'Pisos (Laminado)' : 'Flooring (Laminate)'}</SelectItem>
    <SelectItem value="garage_conversion">{language === 'es' ? 'Conversión de Garaje' : 'Garage Conversion'}</SelectItem>
    <SelectItem value="gutter_installation">{language === 'es' ? 'Instalación de Canaletas' : 'Gutter Installation'}</SelectItem>
    <SelectItem value="home_addition">{language === 'es' ? 'Ampliación del Hogar' : 'Home Addition'}</SelectItem>
    <SelectItem value="home_renovation">{language === 'es' ? 'Renovación del Hogar' : 'Home Renovation'}</SelectItem>
    <SelectItem value="hvac_replacement">{language === 'es' ? 'Reemplazo de HVAC' : 'HVAC Replacement'}</SelectItem>
    <SelectItem value="interior_painting">{language === 'es' ? 'Pintura Interior' : 'Interior Painting'}</SelectItem>
    <SelectItem value="kitchen">{language === 'es' ? 'Remodelación de Cocina' : 'Kitchen Remodeling'}</SelectItem>
    <SelectItem value="mold_remediation">{language === 'es' ? 'Remediación de Moho' : 'Mold Remediation'}</SelectItem>
    <SelectItem value="new_construction">{language === 'es' ? 'Nueva Construcción' : 'New Construction'}</SelectItem>
    <SelectItem value="pool_construction">{language === 'es' ? 'Construcción de Piscina' : 'Pool Construction'}</SelectItem>
    <SelectItem value="popcorn_ceiling_removal">{language === 'es' ? 'Remoción de Techo Popcorn' : 'Popcorn Ceiling Removal'}</SelectItem>
    <SelectItem value="pressure_washing">{language === 'es' ? 'Lavado a Presión' : 'Pressure Washing'}</SelectItem>
    <SelectItem value="roofing">{language === 'es' ? 'Techos' : 'Roofing'}</SelectItem>
    <SelectItem value="room_painting">{language === 'es' ? 'Pintura de Habitación' : 'Room Painting'}</SelectItem>
    <SelectItem value="soffit_fascia">{language === 'es' ? 'Sofito y Fascia' : 'Soffit & Fascia'}</SelectItem>
    <SelectItem value="stucco_repair">{language === 'es' ? 'Reparación de Estuco' : 'Stucco Repair'}</SelectItem>
    <SelectItem value="tile_installation">{language === 'es' ? 'Instalación de Azulejos' : 'Tile Installation'}</SelectItem>
    <SelectItem value="water_heater_replacement">{language === 'es' ? 'Reemplazo de Calentador' : 'Water Heater Replacement'}</SelectItem>
    <SelectItem value="water_mitigation">{language === 'es' ? 'Mitigación de Agua' : 'Water Mitigation'}</SelectItem>
    <SelectItem value="window_replacement">{language === 'es' ? 'Reemplazo de Ventanas' : 'Window Replacement'}</SelectItem>
    {/* Commercial Projects */}
    <SelectItem value="commercial_driveways">{language === 'es' ? '🏢 Comercial - Entradas' : '🏢 Commercial - Driveways'}</SelectItem>
    <SelectItem value="commercial_drywall">{language === 'es' ? '🏢 Comercial - Drywall' : '🏢 Commercial - Drywall'}</SelectItem>
    <SelectItem value="commercial_electrical">{language === 'es' ? '🏢 Comercial - Eléctrico' : '🏢 Commercial - Electrical'}</SelectItem>
    <SelectItem value="commercial_exterior">{language === 'es' ? '🏢 Comercial - Exterior' : '🏢 Commercial - Exterior'}</SelectItem>
    <SelectItem value="commercial_fence">{language === 'es' ? '🏢 Comercial - Cercas' : '🏢 Commercial - Fence'}</SelectItem>
    <SelectItem value="commercial_hvac">{language === 'es' ? '🏢 Comercial - HVAC' : '🏢 Commercial - HVAC'}</SelectItem>
    <SelectItem value="commercial_painting">{language === 'es' ? '🏢 Comercial - Pintura' : '🏢 Commercial - Painting'}</SelectItem>
    <SelectItem value="commercial_plumbing">{language === 'es' ? '🏢 Comercial - Plomería' : '🏢 Commercial - Plumbing'}</SelectItem>
    <SelectItem value="commercial_pool">{language === 'es' ? '🏢 Comercial - Piscina' : '🏢 Commercial - Pool'}</SelectItem>
    <SelectItem value="commercial_roadways">{language === 'es' ? '🏢 Comercial - Carreteras' : '🏢 Commercial - Roadways'}</SelectItem>
    <SelectItem value="commercial_roofing">{language === 'es' ? '🏢 Comercial - Techos' : '🏢 Commercial - Roofing'}</SelectItem>
  </>
);

// Roofing Measurements Section
const RoofingMeasurements = ({ language, aiMeasurements, setAiMeasurements, handleInputChange }) => (
  <div className="space-y-4">
    {/* Basic Roof Info Row */}
    <div className="grid grid-cols-4 gap-4">
      <div>
        <Label className="text-xs text-gray-600">{language === 'es' ? 'Tipo de Techo' : 'Roof Type'}</Label>
        <Select value={aiMeasurements.roof_stories} onValueChange={(v) => setAiMeasurements({...aiMeasurements, roof_stories: v})}>
          <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="1">1 Story</SelectItem>
            <SelectItem value="2">2 Stories</SelectItem>
            <SelectItem value="3">3+ Stories</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-xs text-gray-600">{language === 'es' ? 'Pendiente' : 'Roof Pitch'}</Label>
        <Select value={aiMeasurements.roof_pitch} onValueChange={(v) => setAiMeasurements({...aiMeasurements, roof_pitch: v})}>
          <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="flat">Flat (0-2:12)</SelectItem>
            <SelectItem value="low">Low (3-4:12)</SelectItem>
            <SelectItem value="standard">Standard (5-7:12)</SelectItem>
            <SelectItem value="steep">Steep (8-12:12)</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-xs text-gray-600">{language === 'es' ? 'Área del Techo (pies²)' : 'Roof Area (sq ft)'}</Label>
        <Input 
          type="number"
          value={aiMeasurements.roof_sqft}
          onChange={(e) => setAiMeasurements({...aiMeasurements, roof_sqft: e.target.value})}
          placeholder="e.g., 3000"
          className="mt-1"
        />
      </div>
      <div>
        <Label className="text-xs text-gray-600">{language === 'es' ? 'Tamaño Total (Cuadros)' : 'Total Size (Squares)'}</Label>
        <Input 
          type="text"
          value={aiMeasurements.roof_squares || ''}
          onChange={(e) => {
            setAiMeasurements({...aiMeasurements, roof_squares: e.target.value});
            handleInputChange('roof_squares', e.target.value);
          }}
          placeholder="N/A or number"
          className="mt-1"
        />
        <p className="text-xs text-gray-400 mt-0.5">1 square = 100 sq ft</p>
      </div>
    </div>
    
    {/* Detailed Roof Measurements Row */}
    <div className="border-t pt-4">
      <Label className="text-xs font-semibold text-purple-700 mb-2 block">
        {language === 'es' ? 'Medidas Lineales del Techo (pies lineales)' : 'Linear Roof Measurements (linear feet)'}
        <span className="font-normal text-gray-500 ml-2">- Leave blank or enter N/A for lump sum estimates</span>
      </Label>
      <div className="grid grid-cols-5 gap-3">
        <div>
          <Label className="text-xs text-gray-600">{language === 'es' ? 'Circunferencia' : 'Circumference'}</Label>
          <Input 
            type="text"
            value={aiMeasurements.roof_circumference || ''}
            onChange={(e) => {
              setAiMeasurements({...aiMeasurements, roof_circumference: e.target.value});
              handleInputChange('roof_circumference', e.target.value);
            }}
            placeholder="N/A"
            className="mt-1"
          />
          <p className="text-xs text-gray-400 mt-0.5">Total perimeter</p>
        </div>
        <div>
          <Label className="text-xs text-gray-600">{language === 'es' ? 'Cumbrera' : 'Ridge'}</Label>
          <Input 
            type="text"
            value={aiMeasurements.roof_ridge || ''}
            onChange={(e) => {
              setAiMeasurements({...aiMeasurements, roof_ridge: e.target.value});
              handleInputChange('roof_ridge', e.target.value);
            }}
            placeholder="N/A"
            className="mt-1"
          />
          <p className="text-xs text-gray-400 mt-0.5">Peak/top line</p>
        </div>
        <div>
          <Label className="text-xs text-gray-600">{language === 'es' ? 'Limahoyas' : 'Valleys'}</Label>
          <Input 
            type="text"
            value={aiMeasurements.roof_valleys || ''}
            onChange={(e) => {
              setAiMeasurements({...aiMeasurements, roof_valleys: e.target.value});
              handleInputChange('roof_valleys', e.target.value);
            }}
            placeholder="N/A"
            className="mt-1"
          />
          <p className="text-xs text-gray-400 mt-0.5">Downward joints</p>
        </div>
        <div>
          <Label className="text-xs text-gray-600">{language === 'es' ? 'Limatesas' : 'Hips'}</Label>
          <Input 
            type="text"
            value={aiMeasurements.roof_hips || ''}
            onChange={(e) => {
              setAiMeasurements({...aiMeasurements, roof_hips: e.target.value});
              handleInputChange('roof_hips', e.target.value);
            }}
            placeholder="N/A"
            className="mt-1"
          />
          <p className="text-xs text-gray-400 mt-0.5">Outward corners</p>
        </div>
        <div>
          <Label className="text-xs text-gray-600">{language === 'es' ? 'Tragaluces' : 'Skylights'}</Label>
          <Select value={aiMeasurements.roof_skylights} onValueChange={(v) => {
            setAiMeasurements({...aiMeasurements, roof_skylights: v});
            handleInputChange('roof_skylights', v);
          }}>
            <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="0">0</SelectItem>
              <SelectItem value="1">1</SelectItem>
              <SelectItem value="2">2</SelectItem>
              <SelectItem value="3">3+</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>

    {/* Additional Roof Details */}
    <div className="border-t pt-4">
      <Label className="text-xs font-semibold text-purple-700 mb-2 block">
        {language === 'es' ? 'Detalles Adicionales del Techo' : 'Additional Roof Details'}
      </Label>
      <div className="grid grid-cols-4 gap-3">
        <div>
          <Label className="text-xs text-gray-600">{language === 'es' ? 'Pared Lateral (pies lin.)' : 'Sidewall (linear ft)'}</Label>
          <Input 
            type="text"
            value={aiMeasurements.roof_sidewall || ''}
            onChange={(e) => {
              setAiMeasurements({...aiMeasurements, roof_sidewall: e.target.value});
              handleInputChange('roof_sidewall', e.target.value);
            }}
            placeholder="N/A"
            className="mt-1"
          />
        </div>
        <div>
          <Label className="text-xs text-gray-600">{language === 'es' ? '# Tuberías/Salientes' : '# Pipe/Protrusions'}</Label>
          <Select value={aiMeasurements.roof_pipes} onValueChange={(v) => {
            setAiMeasurements({...aiMeasurements, roof_pipes: v});
            handleInputChange('roof_pipes', v);
          }}>
            <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="0-5">0-5</SelectItem>
              <SelectItem value="6-10">6-10</SelectItem>
              <SelectItem value="11-15">11-15</SelectItem>
              <SelectItem value="16+">16+</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-gray-600">{language === 'es' ? '# Capas a Remover' : '# Layers to Remove'}</Label>
          <Select value={aiMeasurements.roof_layers} onValueChange={(v) => {
            setAiMeasurements({...aiMeasurements, roof_layers: v});
            handleInputChange('roof_layers', v);
          }}>
            <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="0">None (New)</SelectItem>
              <SelectItem value="1">1 Layer</SelectItem>
              <SelectItem value="2">2 Layers</SelectItem>
              <SelectItem value="3">3+ Layers</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-gray-600">{language === 'es' ? 'Reemplazo de Madera' : 'Wood Replacement'}</Label>
          <Select value={aiMeasurements.roof_wood_replacement} onValueChange={(v) => {
            setAiMeasurements({...aiMeasurements, roof_wood_replacement: v});
            handleInputChange('roof_wood_replacement', v);
          }}>
            <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None Expected</SelectItem>
              <SelectItem value="minor">Minor (1-5 sheets)</SelectItem>
              <SelectItem value="moderate">Moderate (6-15 sheets)</SelectItem>
              <SelectItem value="major">Major (16+ sheets)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  </div>
);

// Kitchen Measurements Section
const KitchenMeasurements = ({ language, aiMeasurements, setAiMeasurements, handleInputChange }) => (
  <div className="space-y-4">
    <div className="grid grid-cols-3 gap-4">
      <div>
        <Label className="text-xs text-gray-600">{language === 'es' ? 'Área de Cocina (pies²)' : 'Kitchen Area (sq ft)'}</Label>
        <Input 
          type="text"
          value={aiMeasurements.kitchen_sqft || ''}
          onChange={(e) => {
            setAiMeasurements({...aiMeasurements, kitchen_sqft: e.target.value});
            handleInputChange('kitchen_sqft', e.target.value);
          }}
          placeholder="N/A or number"
          className="mt-1"
        />
      </div>
      <div>
        <Label className="text-xs text-gray-600">{language === 'es' ? 'Complejidad' : 'Complexity'}</Label>
        <Select value={aiMeasurements.kitchen_complexity} onValueChange={(v) => {
          setAiMeasurements({...aiMeasurements, kitchen_complexity: v});
          handleInputChange('kitchen_complexity', v);
        }}>
          <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="simple">{language === 'es' ? 'Simple (solo pintura/pisos)' : 'Simple (paint/floor only)'}</SelectItem>
            <SelectItem value="moderate">{language === 'es' ? 'Moderado (gabinetes menores)' : 'Moderate (minor cabinets)'}</SelectItem>
            <SelectItem value="full">{language === 'es' ? 'Completo (todo nuevo)' : 'Full (all new)'}</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-xs text-gray-600">{language === 'es' ? 'Nivel de Acabado' : 'Finish Level'}</Label>
        <Select value={aiMeasurements.kitchen_finish_level} onValueChange={(v) => {
          setAiMeasurements({...aiMeasurements, kitchen_finish_level: v});
          handleInputChange('kitchen_finish_level', v);
        }}>
          <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="builder">{language === 'es' ? 'Constructor' : 'Builder Grade'}</SelectItem>
            <SelectItem value="standard">{language === 'es' ? 'Estándar' : 'Standard'}</SelectItem>
            <SelectItem value="premium">{language === 'es' ? 'Premium' : 'Premium'}</SelectItem>
            <SelectItem value="luxury">{language === 'es' ? 'Lujo' : 'Luxury'}</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
    <div className="border-t pt-4">
      <Label className="text-xs font-semibold text-purple-700 mb-2 block">
        {language === 'es' ? 'Medidas Lineales (pies lineales)' : 'Linear Measurements (linear feet)'}
        <span className="font-normal text-gray-500 ml-2">- Leave blank or N/A for lump sum</span>
      </Label>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <Label className="text-xs text-gray-600">{language === 'es' ? 'Encimeras' : 'Countertops'}</Label>
          <Input 
            type="text"
            value={aiMeasurements.countertop_lf || ''}
            onChange={(e) => {
              setAiMeasurements({...aiMeasurements, countertop_lf: e.target.value});
              handleInputChange('countertop_lf', e.target.value);
            }}
            placeholder="N/A"
            className="mt-1"
          />
        </div>
        <div>
          <Label className="text-xs text-gray-600">{language === 'es' ? 'Gabinetes' : 'Cabinets'}</Label>
          <Input 
            type="text"
            value={aiMeasurements.cabinet_lf || ''}
            onChange={(e) => {
              setAiMeasurements({...aiMeasurements, cabinet_lf: e.target.value});
              handleInputChange('cabinet_lf', e.target.value);
            }}
            placeholder="N/A"
            className="mt-1"
          />
        </div>
        <div>
          <Label className="text-xs text-gray-600">{language === 'es' ? 'Azulejo/Salpicadero' : 'Tiling/Backsplash'}</Label>
          <Input 
            type="text"
            value={aiMeasurements.backsplash_sqft || ''}
            onChange={(e) => {
              setAiMeasurements({...aiMeasurements, backsplash_sqft: e.target.value});
              handleInputChange('backsplash_sqft', e.target.value);
            }}
            placeholder="N/A"
            className="mt-1"
          />
        </div>
      </div>
    </div>
  </div>
);

// Bathroom Measurements Section  
const BathroomMeasurements = ({ language, aiMeasurements, setAiMeasurements, handleInputChange }) => (
  <div className="space-y-4">
    <div className="grid grid-cols-3 gap-4">
      <div>
        <Label className="text-xs text-gray-600">{language === 'es' ? 'Área del Baño (pies²)' : 'Bathroom Area (sq ft)'}</Label>
        <Input 
          type="text"
          value={aiMeasurements.bathroom_sqft || ''}
          onChange={(e) => {
            setAiMeasurements({...aiMeasurements, bathroom_sqft: e.target.value});
            handleInputChange('bathroom_sqft', e.target.value);
          }}
          placeholder="N/A or number"
          className="mt-1"
        />
      </div>
      <div>
        <Label className="text-xs text-gray-600">{language === 'es' ? 'Número de Baños' : 'Number of Bathrooms'}</Label>
        <Select value={aiMeasurements.num_bathrooms} onValueChange={(v) => {
          setAiMeasurements({...aiMeasurements, num_bathrooms: v});
          handleInputChange('num_bathrooms', v);
        }}>
          <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="1">1</SelectItem>
            <SelectItem value="2">2</SelectItem>
            <SelectItem value="3">3</SelectItem>
            <SelectItem value="4+">4+</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-xs text-gray-600">{language === 'es' ? 'Alcance' : 'Scope'}</Label>
        <Select value={aiMeasurements.bathroom_scope} onValueChange={(v) => {
          setAiMeasurements({...aiMeasurements, bathroom_scope: v});
          handleInputChange('bathroom_scope', v);
        }}>
          <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="cosmetic">{language === 'es' ? 'Cosmético (pintura/accesorios)' : 'Cosmetic (paint/fixtures)'}</SelectItem>
            <SelectItem value="partial">{language === 'es' ? 'Parcial (vanidad/azulejo)' : 'Partial (vanity/tile)'}</SelectItem>
            <SelectItem value="full">{language === 'es' ? 'Completo (todo nuevo)' : 'Full (gut remodel)'}</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
    <div className="border-t pt-4">
      <Label className="text-xs font-semibold text-purple-700 mb-2 block">
        {language === 'es' ? 'Medidas de Azulejo (pies²)' : 'Tile Measurements (sq ft)'}
      </Label>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <Label className="text-xs text-gray-600">{language === 'es' ? 'Piso' : 'Floor'}</Label>
          <Input 
            type="text"
            value={aiMeasurements.floor_tile_sqft || ''}
            onChange={(e) => {
              setAiMeasurements({...aiMeasurements, floor_tile_sqft: e.target.value});
              handleInputChange('floor_tile_sqft', e.target.value);
            }}
            placeholder="N/A"
            className="mt-1"
          />
        </div>
        <div>
          <Label className="text-xs text-gray-600">{language === 'es' ? 'Pared de Ducha' : 'Shower Walls'}</Label>
          <Input 
            type="text"
            value={aiMeasurements.shower_tile_sqft || ''}
            onChange={(e) => {
              setAiMeasurements({...aiMeasurements, shower_tile_sqft: e.target.value});
              handleInputChange('shower_tile_sqft', e.target.value);
            }}
            placeholder="N/A"
            className="mt-1"
          />
        </div>
        <div>
          <Label className="text-xs text-gray-600">{language === 'es' ? 'Vanidad (pies lin.)' : 'Vanity (linear ft)'}</Label>
          <Input 
            type="text"
            value={aiMeasurements.vanity_lf || ''}
            onChange={(e) => {
              setAiMeasurements({...aiMeasurements, vanity_lf: e.target.value});
              handleInputChange('vanity_lf', e.target.value);
            }}
            placeholder="N/A"
            className="mt-1"
          />
        </div>
      </div>
    </div>
  </div>
);

// Flooring Measurements
const FlooringMeasurements = ({ language, aiMeasurements, setAiMeasurements, handleInputChange }) => (
  <div className="grid grid-cols-4 gap-4">
    <div>
      <Label className="text-xs text-gray-600">{language === 'es' ? 'Área de Piso (pies²)' : 'Floor Area (sq ft)'}</Label>
      <Input 
        type="text"
        value={aiMeasurements.flooring_sqft || ''}
        onChange={(e) => {
          setAiMeasurements({...aiMeasurements, flooring_sqft: e.target.value});
          handleInputChange('flooring_sqft', e.target.value);
        }}
        placeholder="N/A or number"
        className="mt-1"
      />
    </div>
    <div>
      <Label className="text-xs text-gray-600">{language === 'es' ? 'Tipo de Piso' : 'Flooring Type'}</Label>
      <Select value={aiMeasurements.flooring_type} onValueChange={(v) => {
        setAiMeasurements({...aiMeasurements, flooring_type: v});
        handleInputChange('flooring_type', v);
      }}>
        <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="lvp">LVP/LVT</SelectItem>
          <SelectItem value="hardwood">{language === 'es' ? 'Madera Dura' : 'Hardwood'}</SelectItem>
          <SelectItem value="laminate">{language === 'es' ? 'Laminado' : 'Laminate'}</SelectItem>
          <SelectItem value="tile">{language === 'es' ? 'Azulejo' : 'Tile'}</SelectItem>
          <SelectItem value="carpet">{language === 'es' ? 'Alfombra' : 'Carpet'}</SelectItem>
        </SelectContent>
      </Select>
    </div>
    <div>
      <Label className="text-xs text-gray-600">{language === 'es' ? 'Rodapié (pies lin.)' : 'Baseboard (linear ft)'}</Label>
      <Input 
        type="text"
        value={aiMeasurements.baseboard_lf || ''}
        onChange={(e) => {
          setAiMeasurements({...aiMeasurements, baseboard_lf: e.target.value});
          handleInputChange('baseboard_lf', e.target.value);
        }}
        placeholder="N/A"
        className="mt-1"
      />
    </div>
    <div>
      <Label className="text-xs text-gray-600">{language === 'es' ? 'Remoción de Existente' : 'Existing Removal'}</Label>
      <Select value={aiMeasurements.flooring_removal} onValueChange={(v) => {
        setAiMeasurements({...aiMeasurements, flooring_removal: v});
        handleInputChange('flooring_removal', v);
      }}>
        <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="none">{language === 'es' ? 'Ninguno' : 'None'}</SelectItem>
          <SelectItem value="carpet">{language === 'es' ? 'Alfombra' : 'Carpet'}</SelectItem>
          <SelectItem value="tile">{language === 'es' ? 'Azulejo' : 'Tile'}</SelectItem>
          <SelectItem value="hardwood">{language === 'es' ? 'Madera' : 'Wood'}</SelectItem>
        </SelectContent>
      </Select>
    </div>
  </div>
);

// Painting Measurements
const PaintingMeasurements = ({ language, aiMeasurements, setAiMeasurements, handleInputChange }) => (
  <div className="grid grid-cols-4 gap-4">
    <div>
      <Label className="text-xs text-gray-600">{language === 'es' ? 'Área de Pintura (pies²)' : 'Paint Area (sq ft)'}</Label>
      <Input 
        type="text"
        value={aiMeasurements.paint_sqft || ''}
        onChange={(e) => {
          setAiMeasurements({...aiMeasurements, paint_sqft: e.target.value});
          handleInputChange('paint_sqft', e.target.value);
        }}
        placeholder="N/A or number"
        className="mt-1"
      />
    </div>
    <div>
      <Label className="text-xs text-gray-600">{language === 'es' ? 'Altura de Pared' : 'Wall Height'}</Label>
      <Select value={aiMeasurements.wall_height} onValueChange={(v) => {
        setAiMeasurements({...aiMeasurements, wall_height: v});
        handleInputChange('wall_height', v);
      }}>
        <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="standard">{language === 'es' ? 'Estándar (8-9 ft)' : 'Standard (8-9 ft)'}</SelectItem>
          <SelectItem value="tall">{language === 'es' ? 'Alto (10-12 ft)' : 'Tall (10-12 ft)'}</SelectItem>
          <SelectItem value="vaulted">{language === 'es' ? 'Abovedado (12+ ft)' : 'Vaulted (12+ ft)'}</SelectItem>
        </SelectContent>
      </Select>
    </div>
    <div>
      <Label className="text-xs text-gray-600">{language === 'es' ? 'Molduras (pies lin.)' : 'Trim (linear ft)'}</Label>
      <Input 
        type="text"
        value={aiMeasurements.trim_lf || ''}
        onChange={(e) => {
          setAiMeasurements({...aiMeasurements, trim_lf: e.target.value});
          handleInputChange('trim_lf', e.target.value);
        }}
        placeholder="N/A"
        className="mt-1"
      />
    </div>
    <div>
      <Label className="text-xs text-gray-600">{language === 'es' ? '# de Capas' : '# of Coats'}</Label>
      <Select value={aiMeasurements.paint_coats} onValueChange={(v) => {
        setAiMeasurements({...aiMeasurements, paint_coats: v});
        handleInputChange('paint_coats', v);
      }}>
        <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="1">1</SelectItem>
          <SelectItem value="2">2</SelectItem>
          <SelectItem value="3">3</SelectItem>
        </SelectContent>
      </Select>
    </div>
  </div>
);

// Fence Measurements
const FenceMeasurements = ({ language, aiMeasurements, setAiMeasurements, handleInputChange }) => (
  <div className="grid grid-cols-5 gap-3">
    <div>
      <Label className="text-xs text-gray-600">{language === 'es' ? 'Largo Total (pies lin.)' : 'Total Length (linear ft)'}</Label>
      <Input 
        type="text"
        value={aiMeasurements.fence_lf || ''}
        onChange={(e) => {
          setAiMeasurements({...aiMeasurements, fence_lf: e.target.value});
          handleInputChange('fence_lf', e.target.value);
        }}
        placeholder="N/A or number"
        className="mt-1"
      />
    </div>
    <div>
      <Label className="text-xs text-gray-600">{language === 'es' ? 'Altura' : 'Height'}</Label>
      <Select value={aiMeasurements.fence_height} onValueChange={(v) => {
        setAiMeasurements({...aiMeasurements, fence_height: v});
        handleInputChange('fence_height', v);
      }}>
        <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="4">4 ft</SelectItem>
          <SelectItem value="6">6 ft</SelectItem>
          <SelectItem value="8">8 ft</SelectItem>
        </SelectContent>
      </Select>
    </div>
    <div>
      <Label className="text-xs text-gray-600">{language === 'es' ? '# Postes' : '# Posts'}</Label>
      <Input 
        type="text"
        value={aiMeasurements.fence_posts || ''}
        onChange={(e) => {
          setAiMeasurements({...aiMeasurements, fence_posts: e.target.value});
          handleInputChange('fence_posts', e.target.value);
        }}
        placeholder="N/A"
        className="mt-1"
      />
    </div>
    <div>
      <Label className="text-xs text-gray-600">{language === 'es' ? '# Puertas' : '# Gates'}</Label>
      <Select value={aiMeasurements.fence_gates} onValueChange={(v) => {
        setAiMeasurements({...aiMeasurements, fence_gates: v});
        handleInputChange('fence_gates', v);
      }}>
        <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="0">0</SelectItem>
          <SelectItem value="1">1</SelectItem>
          <SelectItem value="2">2</SelectItem>
          <SelectItem value="3">3+</SelectItem>
        </SelectContent>
      </Select>
    </div>
    <div>
      <Label className="text-xs text-gray-600">{language === 'es' ? 'Material' : 'Material'}</Label>
      <Select value={aiMeasurements.fence_material} onValueChange={(v) => {
        setAiMeasurements({...aiMeasurements, fence_material: v});
        handleInputChange('fence_material', v);
      }}>
        <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="wood">Wood</SelectItem>
          <SelectItem value="vinyl">Vinyl/PVC</SelectItem>
          <SelectItem value="chain_link">Chain Link</SelectItem>
          <SelectItem value="aluminum">Aluminum</SelectItem>
        </SelectContent>
      </Select>
    </div>
  </div>
);

// General Measurements
const GeneralMeasurements = ({ language, aiMeasurements, setAiMeasurements, handleInputChange }) => (
  <div className="grid grid-cols-2 gap-4">
    <div>
      <Label className="text-xs text-gray-600">{language === 'es' ? 'Área Total (pies²)' : 'Total Area (sq ft)'}</Label>
      <Input 
        type="text"
        value={aiMeasurements.total_sqft || ''}
        onChange={(e) => {
          setAiMeasurements({...aiMeasurements, total_sqft: e.target.value});
          handleInputChange('total_sqft', e.target.value);
        }}
        placeholder="N/A or number"
        className="mt-1"
      />
    </div>
    <div>
      <Label className="text-xs text-gray-600">{language === 'es' ? 'Número de Habitaciones' : 'Number of Rooms'}</Label>
      <Input 
        type="text"
        value={aiMeasurements.num_rooms || ''}
        onChange={(e) => {
          setAiMeasurements({...aiMeasurements, num_rooms: e.target.value});
          handleInputChange('num_rooms', e.target.value);
        }}
        placeholder="N/A or number"
        className="mt-1"
      />
    </div>
  </div>
);

const AIEstimateGeneratorCard = ({
  language,
  lead,
  handleInputChange,
  // AI State
  aiEstimateType,
  setAiEstimateType,
  aiProjectCategory,
  setAiProjectCategory,
  aiQualityTier,
  setAiQualityTier,
  aiRegion,
  setAiRegion,
  aiRoofType,
  setAiRoofType,
  aiMeasurements,
  setAiMeasurements,
  aiPrompt,
  setAiPrompt,
  aiGenerating,
  aiGeneratedEstimate,
  handleGenerateAIEstimate,
  applyAIEstimateToProposal,
  // Voice
  isRecording,
  startRecording,
  stopRecording,
  // Email Writer
  showEmailWriter,
  setShowEmailWriter,
  emailType,
  setEmailType,
  customEmailContext,
  setCustomEmailContext,
  emailGenerating,
  generatedEmail,
  setGeneratedEmail,
  handleGenerateEmail,
  handleSendGeneratedEmail
}) => {
  return (
    <Card className="border-2 border-purple-300 bg-gradient-to-r from-purple-50 to-indigo-50 mb-6">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center text-purple-800">
            <Sparkles className="mr-2 h-5 w-5 text-purple-600" />
            {language === 'es' ? 'Generador de Estimados con IA' : 'AI Estimate Generator'}
            <Badge className="ml-2 bg-purple-600 text-white text-xs">Beta</Badge>
          </CardTitle>
          <div className="flex gap-2">
            <Button 
              type="button" 
              size="sm" 
              variant="outline"
              className="bg-white border-green-300 text-green-600 hover:bg-green-50"
              onClick={() => setShowEmailWriter(!showEmailWriter)}
            >
              <Mail className="h-4 w-4 mr-1" />
              {language === 'es' ? 'Email AI' : 'AI Email'}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Email Writer Panel */}
        <EmailWriterPanel
          language={language}
          lead={lead}
          showEmailWriter={showEmailWriter}
          setShowEmailWriter={setShowEmailWriter}
          emailType={emailType}
          setEmailType={setEmailType}
          customEmailContext={customEmailContext}
          setCustomEmailContext={setCustomEmailContext}
          emailGenerating={emailGenerating}
          generatedEmail={generatedEmail}
          setGeneratedEmail={setGeneratedEmail}
          handleGenerateEmail={handleGenerateEmail}
          handleSendGeneratedEmail={handleSendGeneratedEmail}
        />

        {/* Estimate Type Toggle */}
        <div className="flex items-center gap-4 p-3 bg-white rounded-lg border border-purple-200">
          <Label className="text-sm font-medium text-purple-700">{language === 'es' ? 'Tipo de Estimado:' : 'Estimate Type:'}</Label>
          <div className="flex gap-2">
            <Button
              type="button"
              size="sm"
              variant={aiEstimateType === 'lump_sum' ? 'default' : 'outline'}
              className={aiEstimateType === 'lump_sum' ? 'bg-purple-600 hover:bg-purple-700' : ''}
              onClick={() => {
                setAiEstimateType('lump_sum');
                handleInputChange('estimate_type', 'lump_sum');
              }}
            >
              {language === 'es' ? 'Suma Global' : 'Lump Sum'}
            </Button>
            <Button
              type="button"
              size="sm"
              variant={aiEstimateType === 'itemized' ? 'default' : 'outline'}
              className={aiEstimateType === 'itemized' ? 'bg-purple-600 hover:bg-purple-700' : ''}
              onClick={() => {
                setAiEstimateType('itemized');
                handleInputChange('estimate_type', 'itemized');
              }}
            >
              {language === 'es' ? 'Detallado con Precios' : 'Itemized with Pricing'}
            </Button>
          </div>
          <span className="text-xs text-gray-500 ml-2">
            {aiEstimateType === 'lump_sum' 
              ? (language === 'es' ? '(Solo alcance de trabajo)' : '(Scope of work only)')
              : (language === 'es' ? '(Con precios editables)' : '(With editable pricing)')
            }
          </span>
        </div>
        
        {/* Project Type & Tier Selection */}
        <div className="grid grid-cols-4 gap-4">
          <div>
            <Label className="text-sm text-purple-700">{language === 'es' ? 'Categoría del Proyecto' : 'Project Category'}</Label>
            <Select value={aiProjectCategory} onValueChange={setAiProjectCategory}>
              <SelectTrigger className="bg-white">
                <SelectValue placeholder={language === 'es' ? 'Seleccionar...' : 'Select...'} />
              </SelectTrigger>
              <SelectContent>
                <ProjectCategoryOptions language={language} />
              </SelectContent>
            </Select>
          </div>
          
          {/* Quality Tier - for non-roofing */}
          {aiProjectCategory && aiProjectCategory !== 'roofing' && (
            <div>
              <Label className="text-sm text-purple-700">{language === 'es' ? 'Nivel de Calidad' : 'Quality Tier'}</Label>
              <Select value={aiQualityTier} onValueChange={setAiQualityTier}>
                <SelectTrigger className="bg-white">
                  <SelectValue placeholder={language === 'es' ? 'Seleccionar...' : 'Select...'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="builder_grade">{language === 'es' ? 'Grado Constructor / Económico' : 'Builder Grade / Economy'}</SelectItem>
                  <SelectItem value="standard">{language === 'es' ? 'Estándar' : 'Standard'}</SelectItem>
                  <SelectItem value="premium">{language === 'es' ? 'Premium' : 'Premium'}</SelectItem>
                  <SelectItem value="luxury">{language === 'es' ? 'Lujo' : 'Luxury'}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          
          {/* Roof Type - only for roofing */}
          {aiProjectCategory === 'roofing' && (
            <div>
              <Label className="text-sm text-purple-700">{language === 'es' ? 'Tipo de Techo' : 'Roof Type'}</Label>
              <Select value={aiRoofType} onValueChange={setAiRoofType}>
                <SelectTrigger className="bg-white">
                  <SelectValue placeholder={language === 'es' ? 'Seleccionar...' : 'Select...'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="tile">{language === 'es' ? 'Teja' : 'Tile'}</SelectItem>
                  <SelectItem value="shingle">{language === 'es' ? 'Shingle/Asfalto' : 'Shingle/Asphalt'}</SelectItem>
                  <SelectItem value="metal">{language === 'es' ? 'Metal' : 'Metal'}</SelectItem>
                  <SelectItem value="flat">{language === 'es' ? 'Plano/TPO' : 'Flat/TPO'}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          
          <div>
            <Label className="text-sm text-purple-700">{language === 'es' ? 'Región (Precios)' : 'Region (Pricing)'}</Label>
            <Select value={aiRegion} onValueChange={setAiRegion}>
              <SelectTrigger className="bg-white">
                <SelectValue placeholder={language === 'es' ? 'Seleccionar...' : 'Select...'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="collier">{language === 'es' ? 'Condado Collier (Naples, FL)' : 'Collier County (Naples, FL)'}</SelectItem>
                <SelectItem value="lee">{language === 'es' ? 'Condado Lee (Fort Myers, FL)' : 'Lee County (Fort Myers, FL)'}</SelectItem>
                <SelectItem value="broward">{language === 'es' ? 'Condado Broward (FL)' : 'Broward County (FL)'}</SelectItem>
                <SelectItem value="miami_dade">{language === 'es' ? 'Miami-Dade (FL)' : 'Miami-Dade (FL)'}</SelectItem>
                <SelectItem value="palm_beach">{language === 'es' ? 'Palm Beach (FL)' : 'Palm Beach (FL)'}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Dynamic Measurements based on Project Category */}
        {aiProjectCategory && (
          <div className="p-4 bg-white rounded-lg border border-purple-200">
            <Label className="text-sm font-semibold text-purple-700 mb-3 block">
              <Sparkles className="inline h-4 w-4 mr-1" />
              {language === 'es' ? 'Medidas del Proyecto' : 'Project Measurements'}
            </Label>
            
            {aiProjectCategory === 'roofing' && (
              <RoofingMeasurements
                language={language}
                aiMeasurements={aiMeasurements}
                setAiMeasurements={setAiMeasurements}
                handleInputChange={handleInputChange}
              />
            )}
            
            {aiProjectCategory === 'kitchen' && (
              <KitchenMeasurements
                language={language}
                aiMeasurements={aiMeasurements}
                setAiMeasurements={setAiMeasurements}
                handleInputChange={handleInputChange}
              />
            )}
            
            {aiProjectCategory === 'bathroom' && (
              <BathroomMeasurements
                language={language}
                aiMeasurements={aiMeasurements}
                setAiMeasurements={setAiMeasurements}
                handleInputChange={handleInputChange}
              />
            )}
            
            {aiProjectCategory === 'flooring' && (
              <FlooringMeasurements
                language={language}
                aiMeasurements={aiMeasurements}
                setAiMeasurements={setAiMeasurements}
                handleInputChange={handleInputChange}
              />
            )}
            
            {['interior_painting', 'room_painting'].includes(aiProjectCategory) && (
              <PaintingMeasurements
                language={language}
                aiMeasurements={aiMeasurements}
                setAiMeasurements={setAiMeasurements}
                handleInputChange={handleInputChange}
              />
            )}
            
            {aiProjectCategory === 'fence_installation' && (
              <FenceMeasurements
                language={language}
                aiMeasurements={aiMeasurements}
                setAiMeasurements={setAiMeasurements}
                handleInputChange={handleInputChange}
              />
            )}
            
            {['home_renovation', 'new_construction', 'house_remodeling', 'home_addition', 'general'].includes(aiProjectCategory) && (
              <GeneralMeasurements
                language={language}
                aiMeasurements={aiMeasurements}
                setAiMeasurements={setAiMeasurements}
                handleInputChange={handleInputChange}
              />
            )}
          </div>
        )}
        
        {/* AI Prompt Input */}
        <div className="relative">
          <Label className="text-sm text-purple-700">{language === 'es' ? 'Describe el proyecto' : 'Describe the project'}</Label>
          <div className="flex gap-2 mt-1">
            <Textarea 
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              placeholder={language === 'es' 
                ? 'Ej: Genera un estimado para techo de teja para una casa de 3000 pies cuadrados según código en Naples, Florida' 
                : 'E.g., Generate a tile roof estimate for a house that is 3000 sq ft up to code in Naples, Florida'}
              className="bg-white flex-1 min-h-[80px]"
            />
            <div className="flex flex-col gap-2">
              {/* Voice Input Button */}
              <Button 
                type="button"
                variant={isRecording ? "destructive" : "outline"}
                className={`h-10 w-10 p-0 ${isRecording ? 'animate-pulse' : ''}`}
                onClick={isRecording ? stopRecording : startRecording}
                title={isRecording ? (language === 'es' ? 'Detener' : 'Stop') : (language === 'es' ? 'Grabar voz' : 'Record voice')}
              >
                {isRecording ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
              </Button>
              {/* Generate Button */}
              <Button 
                onClick={handleGenerateAIEstimate}
                disabled={aiGenerating || (!aiPrompt.trim() && !aiProjectCategory)}
                className="bg-purple-600 hover:bg-purple-700 text-white h-10"
              >
                {aiGenerating ? (
                  <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" />
                ) : (
                  <Sparkles className="h-5 w-5" />
                )}
              </Button>
            </div>
          </div>
          {isRecording && (
            <p className="text-sm text-red-600 mt-1 animate-pulse">
              {language === 'es' ? '🎙️ Grabando... Haz clic para detener' : '🎙️ Recording... Click to stop'}
            </p>
          )}
        </div>
        
        {/* AI Generated Result */}
        {aiGeneratedEstimate && (
          <div className="bg-white rounded-lg p-4 border border-purple-200">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-semibold text-purple-800">{aiGeneratedEstimate.project_title || 'Generated Scope of Work'}</h4>
              <Button 
                size="sm" 
                onClick={applyAIEstimateToProposal}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                <Plus className="h-4 w-4 mr-1" />
                {language === 'es' ? 'Aplicar a Propuesta' : 'Apply to Proposal'}
              </Button>
            </div>
            <div className="space-y-3 max-h-[300px] overflow-y-auto">
              {aiGeneratedEstimate.scope_items?.map((section, sIdx) => (
                <div key={sIdx} className="border-l-2 border-purple-300 pl-3">
                  <h5 className="font-medium text-purple-700 text-sm">{section.category}</h5>
                  <ul className="mt-1 space-y-1">
                    {section.items?.map((item, iIdx) => (
                      <li key={iIdx} className="text-sm text-gray-700">
                        <span className="font-medium">{item.title}:</span> {item.description}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            {aiGeneratedEstimate.notes?.length > 0 && (
              <div className="mt-3 pt-3 border-t border-purple-200">
                <p className="text-xs text-purple-600 font-medium">{language === 'es' ? 'Notas:' : 'Notes:'}</p>
                <ul className="text-xs text-gray-600 list-disc list-inside">
                  {aiGeneratedEstimate.notes.map((note, nIdx) => (
                    <li key={nIdx}>{note}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AIEstimateGeneratorCard;
